import React, { useEffect, useState } from 'react'
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  Upload,
  Row,
  Col,
} from 'antd'
import { DeleteOutlined, PlusOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons'
import axios from 'axios'
import { toast } from 'react-toastify'
import { validateImageUpload } from '../../Validation/FileValidation'

import {
  GetJobPost,
  CreateJobPost,
  DeleteJobPost,
  GetJobCategorysMaster,
  GetJobPostByAlumni,
} from '../../AllAPI/AdminAPI'
import TextArea from 'antd/es/input/TextArea'

const { Option } = Select

export default function AlumniJobPost() {
  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [form] = Form.useForm()

  const [fileList, setFileList] = useState([])
  const [categories, setCategories] = useState([])

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState(null)

  const alumniData = JSON.parse(sessionStorage.getItem('alumniData'))
  const alumniSignupId = alumniData?.signup_id
  const year = alumniData?.year

  // ===== FETCH JOBS =====
  const fetchJobs = async () => {
    try {
      setLoading(true)
      const res = await axios.get(
        `${GetJobPostByAlumni}/${alumniSignupId}/${year}`,
      )
      setData(res.data || [])
      setFilteredData(res.data || [])
    } catch {
      toast.error('Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }

  // ===== FETCH CATEGORY =====
  const getCategories = async () => {
    const res = await axios.get(GetJobCategorysMaster)
    setCategories(res.data || [])
  }

  useEffect(() => {
    fetchJobs()
    getCategories()
  }, [])

  // ===== SEARCH =====
  useEffect(() => {
    const filtered = data.filter((item) =>
      item.job_title?.toLowerCase().includes(searchText.toLowerCase()),
    )
    setFilteredData(filtered)
  }, [searchText, data])

  // ===== DELETE =====

  const openDeleteModal = (id) => {
    setSelectedJobId(id)
    setIsDeleteModalOpen(true)
  }

  const confirmDelete = async () => {
    if (!selectedJobId) return

    try {
      const res = await axios.delete(`${DeleteJobPost}/${selectedJobId}`)
      if (res.data === 'DELETED') {
        toast.success('Deleted successfully')
      }
      fetchJobs()
    } catch {
      toast.error('Delete failed')
    } finally {
      setIsDeleteModalOpen(false)
      setSelectedJobId(null)
    }
  }

  // ===== IMAGE VALIDATION =====
  const beforeUpload = (file) => {
    const isLt2MB = file.size / 1024 / 1024 < 2
    if (!isLt2MB) {
      toast.error('Image must be less than 2MB')
      return Upload.LIST_IGNORE
    }
    return false
  }

  // ===== SUBMIT =====
  const onFinish = async () => {
    try {
      const values = await form.validateFields()

      if (fileList.length === 0) {
        toast.error('Upload image')
        return
      }

      const formData = new FormData()

      Object.keys(values).forEach((key) => {
        formData.append(key, values[key])
      })

      formData.append('jobpost_image', fileList[0].originFileObj)
      formData.append('alumini_signup_id', alumniSignupId)
      formData.append('year', new Date().getFullYear())

      await axios.post(CreateJobPost, formData)

      toast.success('Job added')
      setIsModalOpen(false)
      form.resetFields()
      setFileList([])
      fetchJobs()
    } catch {
      toast.error('Error saving job')
    }
  }

  // ===== TABLE COLUMNS =====
  const columns = [
    {
      title: 'Sr No',
      key: 'srno',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Job Title',
      dataIndex: 'job_title',
    },
    {
      title: 'Category',
      render: (_, record) => record.job_category_name?.category_name || '-',
    },
    {
      title: 'Experience',
      dataIndex: 'experience',
    },
    {
      title: 'Industry Name',
      dataIndex: 'industry_name',
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Button icon={<DeleteOutlined />} danger onClick={() => openDeleteModal(record.id)} />
      ), 
    },
  ]

  return (
    <div className="form-container mainContainer">
      <div className="row p-0 m-0 pb-5">
        <div className="col-12 col-md-2"></div>

        <div className="col-12 col-md-8">
          {/* HEADER */}
          <div className="header-row">
            <h2 className="page-title">Job Post</h2>
          </div>

          {/* SEARCH + ADD */}
          <div className="search-bar">
            <Input
              placeholder="Search Job..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />

            <Button
              className="addButton mt-2 mt-md-0"
              icon={<PlusOutlined />}
              onClick={() => setIsModalOpen(true)}
              style={{ marginLeft: 10 }}
            >
              Add Job
            </Button>
          </div>

          {/* TABLE */}
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            bordered
            loading={loading}
            pagination={{ pageSize: 10 }}
            className="shadow border rounded"
             scroll={{ x: 'max-content' }}
          />
        </div>

        <div className="col-12 col-md-2"></div>
      </div>

      {/* ===== MODAL FORM ===== */}
      <Modal
        title="Add Job"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        width={900}
        footer={[
          <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>,
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.submit()} // 🔥 IMPORTANT
          >
            Submit Job
          </Button>,
        ]}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish} // ✅ keep your same function
          style={{
            background: '#f2f7fd',
            padding: 20,
            borderRadius: 10,
          }}
        >
          <Row gutter={16}>
            {/* ===== Category ===== */}
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label="Job Category"
                name="job_category"
                rules={[
                  { required: true, message: 'Job Category is required' },
                ]}
              >
                <Select size="large">
                  {categories.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.category_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* ===== Job Type ===== */}
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label="Job Type"
                name="job_type"
                rules={[{ required: true, message: 'Job Type is required' }]}
              >
                <Select size="large">
                  <Option value={1}>Full Time</Option>
                  <Option value={2}>Part Time</Option>
                  <Option value={3}>Internship</Option>
                </Select>
              </Form.Item>
            </Col>

            {/* ===== Title ===== */}
            <Col xs={24} sm={12} md={12}>
              <Form.Item
                label="Job Title"
                name="job_title"
                rules={[{ required: true, message: 'Job Title is required' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>

            {/* ===== Experience ===== */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Experience"
                name="experience"
                rules={[{ required: true, message: 'Experience is required' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>

            {/* ===== Industry ===== */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Industry Name"
                name="industry_name"
                rules={[
                  { required: true, message: 'Industry Name is required' },
                ]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>

            {/* ===== Contact ===== */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Contact No"
                name="contact"
                rules={[
                  { required: true, message: 'Contact no is required' },
                  {
                    pattern: /^[0-9]{10}$/,
                    message: 'Contact must be 10 digits',
                  },
                ]}
              >
                <Input
                  size="large"
                  maxLength={10}
                  inputMode="numeric"
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, '')
                  }}
                />
              </Form.Item>
            </Col>

            {/* ===== Vacancy ===== */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Vacancy"
                name="vaccancy"
                rules={[{ required: true, message: 'Vacancy is required' }]}
              >
                <Input
                  size="large"
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, '')
                  }}
                />
              </Form.Item>
            </Col>

            {/* ===== Qualification ===== */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Qualification"
                name="qualification"
                rules={[{ required: true }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>

            {/* ===== Image Upload ===== */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="Upload Job Image" required>
                <Upload
  beforeUpload={validateImageUpload}
                  style={{ width: '100%' }}
                  accept="image/*"
                  beforeUpload={beforeUpload}
                  fileList={fileList}
                  onChange={({ fileList }) => setFileList(fileList)}
                  listType="picture"
                  maxCount={1}
                >
                  <Button
                    style={{ width: '100%' }}
                    icon={<UploadOutlined />}
                    size="large"
                    block
                  >
                    Upload
                  </Button>
                </Upload>
              </Form.Item>
            </Col>

            {/* ===== Description ===== */}
            <Col xs={24}>
              <Form.Item
                label="Description"
                name="discription"
                rules={[{ required: true, message: 'Description required' }]}
              >
                <TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ===== DELETE MODAL ===== */}
      <Modal
        title="Confirm Delete"
        open={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        footer={[
          <Button onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>,
          <Button type="primary" danger onClick={confirmDelete}>
            Yes, Delete
          </Button>,
        ]}
      >
        <p>Are you sure you want to delete this job?</p>
        <p>This action cannot be undone.</p>
      </Modal>
    </div>
  )
}
